import { memo, useCallback, useEffect, useMemo, useState } from "react";
import "./CollapsibleAuditActionsTable.scss";

export type CollapsibleAuditActionsRow = {
  category: string;
  section: string;
  notes: string;
  actions: string;
};

interface CollapsibleAuditActionsTableProps {
  rows: CollapsibleAuditActionsRow[];
  resetToken?: string;
}

const DEFAULT_SECTION_LABEL = "General";

const CollapsibleAuditActionsTable = memo((props: CollapsibleAuditActionsTableProps) => {
  const categories = useMemo(() => {
    // Preserve insertion order from incoming rows.
    const categoryMap = new Map<
      string,
      {
        category: string;
        sections: Map<string, CollapsibleAuditActionsRow[]>;
      }
    >();

    props.rows.forEach((row) => {
      const rawCategory = typeof row.category === "string" ? row.category.trim() : "";
      const rawSection = typeof row.section === "string" ? row.section.trim() : "";

      const category = rawCategory || "";
      const section = rawSection || DEFAULT_SECTION_LABEL;

      const existingCategory = categoryMap.get(category);
      if (!existingCategory) {
        categoryMap.set(category, {
          category,
          sections: new Map<string, CollapsibleAuditActionsRow[]>(),
        });
      }

      const catEntry = categoryMap.get(category)!;
      const existingSectionRows = catEntry.sections.get(section) ?? [];
      catEntry.sections.set(section, [...existingSectionRows, row]);
    });

    return Array.from(categoryMap.values()).map((c) => ({
      category: c.category,
      sections: Array.from(c.sections.entries()).map(([section, rows]) => ({ section, rows })),
    }));
  }, [props.rows]);

  const [collapsedCategories, setCollapsedCategories] = useState<Record<string, boolean>>({});
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});

  // Every time a new audit is loaded, force all categories and sections collapsed by default.
  useEffect(() => {
    const nextCollapsedCategories: Record<string, boolean> = {};
    const nextCollapsedSections: Record<string, boolean> = {};

    categories.forEach((cat) => {
      nextCollapsedCategories[cat.category] = true;
      cat.sections.forEach((sec) => {
        nextCollapsedSections[`${cat.category}||${sec.section}`] = true;
      });
    });

    setCollapsedCategories(nextCollapsedCategories);
    setCollapsedSections(nextCollapsedSections);
  }, [props.resetToken]);

  const toggleCategory = useCallback((category: string) => {
    setCollapsedCategories((current) => ({
      ...current,
      [category]: !current[category],
    }));
  }, []);

  const toggleSection = useCallback((category: string, section: string) => {
    const key = `${category}||${section}`;
    setCollapsedSections((current) => ({
      ...current,
      [key]: !current[key],
    }));
  }, []);

  if (!props.rows || props.rows.length === 0) return null;

  return (
    <div className="sc-collapsible-audit-actions">
      <div className="sc-ca-header">
        <div>Audit Responses</div>
        <div>Recommendations</div>
      </div>

      <div className="sc-ca-body">
        {categories.map((cat, catIdx) => {
          const isCategoryCollapsed = Boolean(collapsedCategories[cat.category]);
          const categoryLabel = cat.category || "";
          const categoryCaret = isCategoryCollapsed ? "▸" : "▾";

          return (
            <div key={`cat-${catIdx}-${cat.category}`}>
              <button
                type="button"
                className="sc-ca-toggle sc-ca-category"
                onClick={() => toggleCategory(cat.category)}
                aria-expanded={!isCategoryCollapsed}
              >
                <span className="sc-ca-caret">{categoryCaret}</span>
                <span className="sc-ca-label">{categoryLabel}</span>
              </button>

              {!isCategoryCollapsed && (
                <div>
                  {cat.sections.map((sec, secIdx) => {
                    const sectionKey = `${cat.category}||${sec.section}`;
                    const isSectionCollapsed = Boolean(collapsedSections[sectionKey]);
                    const sectionCaret = isSectionCollapsed ? "▸" : "▾";

                    return (
                      <div key={`sec-${catIdx}-${secIdx}-${sectionKey}`}>
                        <button
                          type="button"
                          className="sc-ca-toggle sc-ca-section"
                          onClick={() => toggleSection(cat.category, sec.section)}
                          aria-expanded={!isSectionCollapsed}
                        >
                          <span className="sc-ca-caret">{sectionCaret}</span>
                          <span className="sc-ca-label">{sec.section}</span>
                        </button>

                        {!isSectionCollapsed && (
                          <div>
                            {sec.rows.map((row, idx) => (
                              <div key={`row-${sectionKey}-${idx}`} className="sc-ca-row">
                                <div className="sc-ca-cell">{row.notes}</div>
                                <div className="sc-ca-cell">{row.actions}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="sc-ca-divider" />
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default CollapsibleAuditActionsTable;
